import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import singleSpaReact from 'single-spa-react'
import { LayoutRoot } from './layout-root'
import { Container, Content } from './components/container'
import { AuthGuard, AuthProvider } from '@yuno/dashboard-authentication-mfe'
import './utils/monitoring'
import { ThemeProvider, theme, Box } from '@yuno/design-system-web'
import ErrorConnection from './components/error-connection'
import { i18nextInit } from './lang-config'
import { I18nextProvider, useTranslation } from 'react-i18next'
import { useInitLang } from './yuno-dashboard-container-mfe'
import i18next from 'i18next'
import en from './lang/en.json'
import es from './lang/es.json'
import port from './lang/pt.json'

// Bootstrap ui loader
localStorage.setItem('DRAWER', 'opened')

const RootComponent = () => {
  const location = window.location.pathname

  useEffect(() => {
    return () => {
      localStorage.removeItem('ERROR_NAV')
    }
  }, [])

  return (
    <>
      {location.includes('/auth') ? null : (
        <AuthProvider>
          <AuthGuard>
            <LayoutRoot />
          </AuthGuard>
        </AuthProvider>
      )}
    </>
  )
}
const isMappedError = (e: any) => {
  return [
    'ERR_BAD_OPTION_VALUE',
    'ERR_BAD_OPTION',
    'ECONNABORTED',
    'ETIMEDOUT',
    'ERR_NETWORK',
    'ERR_FR_TOO_MANY_REDIRECTS',
    'ERR_DEPRECATED',
    'ERR_BAD_RESPONSE',
    'ERR_BAD_REQUEST',
    'ERR_CANCELED',
    'ERR_NOT_SUPPORT',
    'ERR_INVALID_URL',
  ].includes(e.code)
}

export const ErrorLottie = ({
  title = null,
  description = null,
  pt = 15,
  open = true,
  containerError = false,
}) => {
  const { t } = useTranslation()
  const i18Instance = useInitLang({ i18n: i18next, en, es, pt: port })
  useEffect(() => {
    localStorage.setItem('ERROR_NAV', containerError ? '1' : '0')
    const event = new StorageEvent('storage', {
      key: 'ERROR_NAV',
      newValue: containerError ? '1' : '0',
    })
    dispatchEvent(event)
  }, [])

  return (
    <ThemeProvider theme={theme}>
      <I18nextProvider i18n={i18Instance}>
        <Content open={open} containerError={containerError}>
          <Box pt={pt} position="relative" left={containerError && '-10%'}>
            <ErrorConnection title={'errorTitle'} description={'errorDesc'} />
          </Box>
        </Content>
      </I18nextProvider>
    </ThemeProvider>
  )
}
const lifeCycles = singleSpaReact({
  React,
  ReactDOM,
  rootComponent: RootComponent,
  errorBoundary(err) {
    if (isMappedError(err)) return <ErrorLottie containerError={true} />
    return <RootComponent />
  },
})

export const errorBoundaryDefault = (DefaultComponent) => {
  const FunctionHOC = (err: any) => {
    if (isMappedError(err)) return null
    return <DefaultComponent />
  }
  return FunctionHOC
}

export const { bootstrap, mount, unmount } = lifeCycles

export { useDialogContext, DialogProvider } from './components/layout/dialog'

// COMPONENTS
export { LayoutRoot as Layout } from './layout-root'
export { Container }
export { MFEWrapper } from './components/mfe-wrapper'
export { ErrorHandlerRedirect } from './components/error-handler-redirect'
export { BreadcrumbPortal } from './components/breadcrumb/bread-crumb-portal'
export { CaretIconStyled } from './components/breadcrumb/caret-icon'
export { LoadingPulse } from './components/loading-pulse'

// CONTEXT
export {
  ContainerProvider,
  ContainerContext,
} from './context/container-context'
export { ImageProvider, useImagesContext } from './context/image-context'
export { FormProvider, FormContext } from './context/form-context'

// LIBS
export * from './lib/storage'
export { i18nextInit }
export { useTranslation, I18nextProvider } from 'react-i18next'
export { useInitLang } from './hooks/use-init-lang'

// HOOKS
export {
  useSnackbarContext,
  SnackbarProvider,
} from './components/layout/snackbar'
export { useReports } from './components/notifications/hooks/reports'
export * from './hooks/use-account'
export * from './hooks/use-organization'
export * from './hooks/use-verify-permission.hook'
export * from './hooks/use-is-testing'
export { useBreadcrumb } from './components/layout'
export { useToggleEnvironment } from './hooks/use-toggle-environment'
export { useClearFilterEvent } from './hooks/use-clear-filter-event'
